// ===========================================
// Teacher Performance Summary Cards Component
// ===========================================
// Display key teacher performance metrics using StatWidget cards

import { Grid } from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as TasksIcon,
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

import { TeacherPerformanceSummary, OutstandingTaskCounts } from '../../api/reports.api';
import { StatWidget } from '../dashboard/StatWidget';
import { formatCurrency, formatPercentage } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

interface TeacherPerformanceSummaryCardsProps {
  summary: TeacherPerformanceSummary;
  loading?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function TeacherPerformanceSummaryCards({
  summary,
  loading = false,
}: TeacherPerformanceSummaryCardsProps) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {/* Outstanding Tasks — the dashboard's definition (this week), coral when any */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Outstanding Tasks"
          value={summary.outstandingTasks?.total ?? 0}
          icon={<TasksIcon />}
          color={(summary.outstandingTasks?.total ?? 0) > 0 ? 'coral' : 'success'}
          subtitle={describeTaskCounts(summary.outstandingTasks)}
          highlight={(summary.outstandingTasks?.total ?? 0) > 0}
          loading={loading}
        />
      </Grid>
      {/* Total Teachers */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Total Teachers"
          value={summary.totalTeachers}
          icon={<PersonIcon />}
          color="primary"
          loading={loading}
        />
      </Grid>

      {/* Total Lessons Delivered */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Lessons Delivered"
          value={summary.totalLessonsDelivered}
          icon={<SchoolIcon />}
          color="info"
          subtitle={`${summary.totalStudentsTaught} students`}
          loading={loading}
        />
      </Grid>

      {/* Average Attendance Rate */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Attendance Rate"
          value={formatPercentage(summary.averageAttendanceRate)}
          icon={<CheckIcon />}
          color="success"
          subtitle="average"
          loading={loading}
        />
      </Grid>

      {/* Total Students Taught */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Students Taught"
          value={summary.totalStudentsTaught}
          icon={<PeopleIcon />}
          color="secondary"
          loading={loading}
        />
      </Grid>

      {/* Total Revenue Generated */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Total Revenue"
          value={formatCurrency(summary.totalRevenueGenerated)}
          icon={<MoneyIcon />}
          color="primary"
          loading={loading}
        />
      </Grid>

      {/* Average Revenue Per Teacher */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Avg / Teacher"
          value={formatCurrency(summary.averageRevenuePerTeacher)}
          icon={<TrendingUpIcon />}
          color="info"
          loading={loading}
        />
      </Grid>
    </Grid>
  );
}

/** "3 attendance · 2 class notes · 4 student notes" — or "this week" when clear. */
export function describeTaskCounts(c: OutstandingTaskCounts | undefined): string {
  if (!c || c.total === 0) return 'this week';
  const parts: string[] = [];
  if (c.attendance > 0) parts.push(`${c.attendance} attendance`);
  if (c.classNotes > 0) parts.push(`${c.classNotes} class note${c.classNotes === 1 ? '' : 's'}`);
  if (c.studentNotes > 0) parts.push(`${c.studentNotes} student note${c.studentNotes === 1 ? '' : 's'}`);
  return parts.join(' · ') || 'this week';
}
